import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Outlet } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import type { Document } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useDocumentsList, useDocumentRemove, DocumentsParamsType } from '../api/documents'
import { Button, ButtonLink } from '../components/button'
import { Input } from '../components/input'
import { Paper } from '../components/paper'
import { Sidepanel, SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'

type Filter = Pick<DocumentsParamsType, 'id' | 'name'>

export function ReservationDocuments() {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<Filter>({})

	const query = useDocumentsList({ ...params, ...filter })
	const remove = useDocumentRemove()
	const canEditDocuments = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_DOCUMENTS)
	const canDeleteDocuments = useHasAccountPermission(ACCOUNT_PERMISSION.DELETE_DOCUMENTS)

	const onRemove = (id: string) => {
		if (confirm('Opravdu chcete smazat dokument?')) {
			remove.mutate(id)
		}
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">Seznam dokumentů</h3>
				<DialogTrigger>
					<Button variant="SECONDARY">Filrovat</Button>
					<Sidepanel heading="Filtrovat">
						{({ close }) => (
							<DocumentsSidepanel
								onClose={close}
								filter={filter}
								onSubmit={setFilter}
							/>
						)}
					</Sidepanel>
				</DialogTrigger>
			</div>
			<List<Document>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<ListHeader sortBy="name">Název</ListHeader>
						<th></th>
					</tr>
				}
			>
				{document => (
					<tr key={document.id}>
						<td width="10%">{document.id}</td>
						<td width="20%">{document.name}</td>
						<td width="20%" className="space-x-2">
							<ButtonLink
								isDisabled={!canEditDocuments}
								variant="SECONDARY"
								size="SMALL"
								to={document.id}
							>
								Upravit
							</ButtonLink>
							<ButtonLink
								isDisabled={!canEditDocuments}
								variant="SECONDARY"
								size="SMALL"
								to={`inspect/${document.id}`}
							>
								Detail
							</ButtonLink>
							<Button
								isDisabled={!canDeleteDocuments}
								variant="DANGER"
								size="SMALL"
								onPress={() => onRemove(document.id)}
							>
								Smazat
							</Button>
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}

function DocumentsSidepanel({
	filter,
	onClose,
	onSubmit,
}: {
	filter: Filter
	onClose: VoidFunction
	onSubmit: (filter: Filter) => void
}) {
	const [id, setId] = useState<string>(filter.id ?? '')
	const [name, setName] = useState<string>(filter.name ?? '')

	return (
		<>
			<SidepanelContent className="space-y-4">
				<Input label="ID" value={id} onChange={setId}></Input>
				<Input label="Název" value={name} onChange={setName}></Input>
			</SidepanelContent>
			<SidepanelFooter>
				<Button
					variant="PRIMARY"
					onPress={() => {
						onClose()
						onSubmit({ id, name })
					}}
				>
					Povrdit
				</Button>
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => {
						onClose()
						onSubmit({})
					}}
				>
					Odstranit vše
				</Button>
			</SidepanelFooter>
		</>
	)
}
